import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Minus, Plus, ShoppingBag, MapPin, CalendarDays, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useCart, fmtPrice } from "@/lib/cart";
import { Reveal } from "@/components/Reveal";
import { ProductImage } from "@/pages/Shop";

export default function ProductDetail() {
  const { id } = useParams();
  const [products, setProducts] = useState(null);
  const [variant, setVariant] = useState("");
  const [qty, setQty] = useState(1);
  const { add } = useCart();

  useEffect(() => {
    api.get("/shop/products").then((r) => setProducts(r.data)).catch(() => setProducts([]));
  }, []);

  const product = useMemo(() => (products || []).find((p) => p.id === id), [products, id]);
  const variants = product?.variant_list || [];
  const selected = variants.find((v) => v.label === variant);
  const unitCents = selected ? selected.price_cents : Math.round(((product?.price || 0)) * 100);
  const others = (products || []).filter((p) => p.id !== id).slice(0, 3);
  const status = ((product?.status) || "AVAILABLE").toUpperCase();

  useEffect(() => {
    if (product && variants.length > 0 && !variant) setVariant(variants[0].label);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product]);

  if (products === null) {
    return <div className="container-site py-24 text-center text-slate-400 text-sm" data-testid="product-loading">Loading…</div>;
  }
  if (!product) {
    return (
      <div className="container-site py-24 text-center" data-testid="product-not-found">
        <p className="text-slate-500">This product isn&rsquo;t available.</p>
        <Link to="/shop" className="mt-4 inline-block text-pool font-semibold text-sm hover:underline" data-testid="product-back">← Back to the Shop</Link>
      </div>
    );
  }

  const purchasable = status === "AVAILABLE" && unitCents > 0 && !product.sold_out;
  const externalPurchase = status === "AVAILABLE" && unitCents <= 0 && !!product.external_link;

  const addToCart = () => {
    add(product, variant, qty, unitCents);
    toast.success(`${product.name} added to your bag`);
  };

  return (
    <div data-testid="product-page">
      <section className="bg-white py-12 sm:py-16">
        <div className="container-site">
          <Link to="/shop" className="inline-flex items-center gap-2 text-slate-400 hover:text-pool text-xs tracking-[0.15em] uppercase font-bold transition-colors mb-8" data-testid="product-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Shop
          </Link>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <Reveal>
              <div className="relative aspect-square overflow-hidden rounded-2xl bg-pool-light/20 shadow-lg" data-testid="product-image">
                <ProductImage p={product} className="absolute inset-0 h-full w-full" />
                {product.sold_out && (
                  <span className="absolute top-4 left-4 bg-ink text-white text-[10px] tracking-[0.2em] uppercase font-bold px-4 py-2 rounded-full">Sold Out</span>
                )}
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-[10px] tracking-[0.25em] uppercase font-bold text-pool" data-testid="product-category">{product.category} · {product.kind}</p>
              <h1 className="mt-2 font-display font-black tracking-tight text-3xl sm:text-4xl text-ink leading-tight" data-testid="product-title">{product.name}</h1>
              {status === "COMING SOON" ? (
                <span className="mt-4 inline-block bg-ink text-white text-[10px] tracking-[0.25em] uppercase font-bold px-5 py-2 rounded-full" data-testid="product-coming-soon">Coming Soon</span>
              ) : !externalPurchase ? (
                <p className="mt-3 text-2xl font-display font-black text-ink" data-testid="product-price">{unitCents > 0 ? fmtPrice(unitCents) : "Price TBA"}</p>
              ) : null}
              {product.description && <p className="mt-5 text-slate-600 leading-relaxed text-sm sm:text-base" data-testid="product-description">{product.description}</p>}

              {variants.length > 0 && (
                <div className="mt-7" data-testid="product-variants">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-2.5">
                    {product.kind === "Gift Card" ? "Select an amount" : "Select an option"}
                  </p>
                  <div className="flex flex-wrap gap-2.5">
                    {variants.map((v) => (
                      <button
                        key={v.label}
                        onClick={() => setVariant(v.label)}
                        data-testid={`variant-${v.label.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        className={`text-xs font-bold px-5 py-2.5 rounded-full border transition-colors ${
                          variant === v.label ? "bg-pool text-white border-pool" : "bg-white text-slate-600 border-slate-200 hover:border-pool"
                        }`}
                      >
                        {v.label}{v.price_cents > 0 && product.kind === "Gift Card" ? "" : ""}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {status === "COMING SOON" ? (
                <div className="mt-7 bg-pool-light/40 border border-pool/25 rounded-2xl p-5" data-testid="product-comingsoon-note">
                  <p className="text-sm text-pool-deep font-semibold">This piece is part of The Poolside Collection — launching soon.</p>
                  <p className="mt-1 text-xs text-slate-500">Join Stay Poolside below for first access and launch updates.</p>
                </div>
              ) : externalPurchase ? (
              <div className="mt-7 flex flex-wrap gap-3" data-testid="product-buy-external">
                <a
                  href={product.external_link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2.5 bg-pool hover:bg-pool-dark text-white font-bold text-xs tracking-[0.15em] uppercase px-7 py-4 rounded-full transition-colors"
                  data-testid="product-buy-amazon"
                >
                  Buy on Amazon <ArrowUpRight className="h-4 w-4" />
                </a>
                {product.external_link_2 && (
                  <a
                    href={product.external_link_2}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2.5 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold text-xs tracking-[0.15em] uppercase px-7 py-4 rounded-full transition-colors"
                    data-testid="product-buy-bn"
                  >
                    Barnes &amp; Noble <ArrowUpRight className="h-4 w-4" />
                  </a>
                )}
              </div>
              ) : (
              <div className="mt-7 flex items-center gap-5">
                <div className="inline-flex items-center border border-slate-200 rounded-full" data-testid="product-quantity">
                  <button onClick={() => setQty(Math.max(1, qty - 1))} className="p-3 text-slate-500 hover:text-pool" aria-label="Decrease quantity" data-testid="qty-minus"><Minus className="h-4 w-4" /></button>
                  <span className="w-8 text-center text-sm font-bold" data-testid="qty-value">{qty}</span>
                  <button onClick={() => setQty(Math.min(20, qty + 1))} className="p-3 text-slate-500 hover:text-pool" aria-label="Increase quantity" data-testid="qty-plus"><Plus className="h-4 w-4" /></button>
                </div>
                <button
                  onClick={addToCart}
                  disabled={!purchasable}
                  className="flex-1 inline-flex items-center justify-center gap-2.5 bg-pool hover:bg-pool-dark disabled:bg-slate-200 disabled:text-slate-400 text-white font-bold text-xs tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors"
                  data-testid="product-add-to-cart"
                >
                  <ShoppingBag className="h-4 w-4" /> {product.sold_out ? "Sold Out" : unitCents > 0 ? "Add to Cart" : "Pricing Coming Soon"}
                </button>
              </div>
              )}

              {product.external_link && !externalPurchase && (
                <a
                  href={product.external_link}
                  className="mt-4 inline-flex items-center gap-2 text-pool font-bold text-xs tracking-[0.15em] uppercase hover:underline"
                  data-testid="product-external-link"
                >
                  {product.kind === "Experience" ? "Book through our Events page →" : "Learn more →"}
                </a>
              )}

              <div className="mt-8 border-t border-slate-100 pt-6 space-y-3 text-xs text-slate-500" data-testid="product-fulfillment-info">
                {product.kind === "Experience" ? (
                  <>
                    {product.experience_date && <p className="flex items-center gap-2.5"><CalendarDays className="h-4 w-4 text-pool" /> {product.experience_date}</p>}
                    {product.experience_location && <p className="flex items-center gap-2.5"><MapPin className="h-4 w-4 text-pool" /> {product.experience_location}</p>}
                    <p className="flex items-center gap-2.5"><Mail className="h-4 w-4 text-pool" /> Confirmation delivered by email after purchase.</p>
                  </>
                ) : product.kind === "Gift Card" || product.kind === "Digital Product" ? (
                  <p className="flex items-center gap-2.5"><Mail className="h-4 w-4 text-pool" /> Delivered digitally by email — no shipping needed.</p>
                ) : externalPurchase ? (
                  <p className="flex items-center gap-2.5"><MapPin className="h-4 w-4 text-pool" /> Sold and shipped by the retailer.</p>
                ) : (
                  <>
                    <p className="flex items-center gap-2.5"><MapPin className="h-4 w-4 text-pool" /> Free pickup at the Poolside Cooking Café, Virginia Beach.</p>
                    <p>Shipping available within the US. Shipping calculated at checkout.</p>
                  </>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {others.length > 0 && (
        <section className="bg-cream py-14 sm:py-16" data-testid="product-related">
          <div className="container-site">
            <Reveal className="mb-8">
              <h2 className="font-display font-black tracking-tight text-2xl sm:text-3xl text-ink">You May Also <span className="text-pool">Like.</span></h2>
            </Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {others.map((p) => (
                <Link key={p.id} to={`/shop/${p.id}`} className="group bg-white rounded-2xl overflow-hidden border border-slate-100 hover:shadow-lg transition-all" data-testid={`related-${p.id}`}>
                  <div className="relative aspect-[4/3] overflow-hidden bg-pool-light/20">
                    <ProductImage p={p} className="absolute inset-0 h-full w-full" />
                  </div>
                  <div className="p-4">
                    <p className="text-[9px] tracking-[0.25em] uppercase font-bold text-pool">{p.category}</p>
                    <p className="mt-1 font-display font-black text-ink text-sm leading-tight group-hover:text-pool transition-colors">{p.name}</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
